import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useSalveazaTargetareListaFisier } from "@/hooks/api/mutate/useSalveazaTargetareListaFisier";
import { useToast } from "@/hooks/use-toast";
import type {
  TargetareListaFisier,
  TargetareMetodaSursa,
  TargetareServiciuScurt,
  TargetareTipLista,
} from "@/types/targetare";
import {
  ETICHETE_TIP_LISTA,
  campuriDinText,
  textDinCampuri,
} from "@/utils/targetareConfig";
import { formatDataOra, formatNumar } from "@/utils/targetareFormatting";
import { SelectServiciu } from "./CelulaLista";

type DetaliiListaDialogProps = {
  lista: TargetareListaFisier | null;
  servicii: TargetareServiciuScurt[];
  onClose: () => void;
};

export function DetaliiListaDialog({
  lista,
  servicii,
  onClose,
}: DetaliiListaDialogProps) {
  const { toast } = useToast();
  const salveaza = useSalveazaTargetareListaFisier();
  const [serviceId, setServiceId] = useState<string | null>(null);
  const [tip, setTip] = useState<TargetareTipLista>("NEOFICIALA");
  const [activ, setActiv] = useState(true);
  const [sursaUrl, setSursaUrl] = useState("");
  const [sursaNume, setSursaNume] = useState("");
  const [autoritate, setAutoritate] = useState("");
  const [descriere, setDescriere] = useState("");
  const [metoda, setMetoda] = useState<TargetareMetodaSursa>("GET");
  const [pagina, setPagina] = useState("");
  const [campuri, setCampuri] = useState("");
  const [coloanaServiciu, setColoanaServiciu] = useState("");
  const [automata, setAutomata] = useState(false);
  const [foaie, setFoaie] = useState("");

  useEffect(() => {
    if (!lista) return;
    setServiceId(lista.service_id);
    setTip(lista.tip);
    setActiv(lista.activ);
    setSursaUrl(lista.sursa_url ?? "");
    setSursaNume(lista.sursa_nume ?? "");
    setAutoritate(lista.autoritate ?? "");
    setDescriere(lista.descriere ?? "");
    setMetoda(lista.sursa_metoda ?? "GET");
    setPagina(lista.sursa_pagina ?? "");
    setCampuri(textDinCampuri(lista.sursa_campuri));
    setColoanaServiciu(lista.coloana_serviciu ?? "");
    setAutomata(lista.actualizare_automata);
    setFoaie(lista.foaie ?? "");
  }, [lista]);

  const peRand = Boolean(
    lista && lista.service_id === null && lista.servicii_randuri.length > 0
  );

  const confirma = () => {
    if (!lista) return;
    salveaza.mutate(
      {
        nume_fisier: lista.nume_fisier,
        service_id: serviceId ?? "",
        tip,
        activ,
        sursa_url: sursaUrl,
        sursa_nume: sursaNume,
        autoritate,
        descriere,
        sursa_metoda: metoda,
        sursa_pagina: pagina,
        sursa_campuri: campuriDinText(campuri),
        coloana_serviciu: coloanaServiciu,
        actualizare_automata: automata,
        foaie,
      },
      {
        onSuccess: (r) => {
          toast({
            title: "Detaliile listei au fost salvate",
            description: `${formatNumar(r.data.potrivite)} rânduri potrivite. Reîncadrarea aplică schimbarea în tabelul de firme.`,
          });
          onClose();
        },
        onError: (e) =>
          toast({
            title: "Salvarea a eșuat",
            description: e.message,
            variant: "destructive",
          }),
      }
    );
  };

  return (
    <Dialog open={lista !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Detaliile listei</DialogTitle>
          <DialogDescription>
            {lista?.nume_fisier}
            {lista?.importat_la &&
              ` · actualizată la ${formatDataOra(lista.importat_la)}`}
            {lista && ` · ${formatNumar(lista.randuri ?? 0)} rânduri`}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-1 text-xs md:col-span-2">
            <span className="text-muted-foreground">
              Serviciul NIS2 al listei
              {peRand &&
                ` (acum: serviciul fiecărui rând, ${lista?.servicii_randuri.length} servicii: ${lista?.servicii_randuri.join(", ")}; alegerea unui serviciu aici îl aplică tuturor rândurilor)`}
            </span>
            <SelectServiciu
              valoare={serviceId}
              servicii={servicii}
              onChange={setServiceId}
              latime="w-full"
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Tipul listei</span>
            <Select
              value={tip}
              onValueChange={(t) => setTip(t as TargetareTipLista)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(["OFICIALA", "NEOFICIALA"] as TargetareTipLista[]).map(
                  (t) => (
                    <SelectItem key={t} value={t}>
                      {ETICHETE_TIP_LISTA[t]}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </label>
          <label className="flex items-center gap-2 self-end pb-1 text-sm">
            <Switch checked={activ} onCheckedChange={setActiv} />
            Lista contează la încadrare
          </label>
          <label className="space-y-1 text-xs md:col-span-2">
            <span className="text-muted-foreground">
              Adresa sursei (fișierul sau pagina de unde se actualizează)
            </span>
            <Input
              value={sursaUrl}
              onChange={(e) => setSursaUrl(e.target.value)}
              placeholder="https://…"
              className="h-8 font-mono text-xs"
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Cum se preia</span>
            <Select
              value={metoda}
              onValueChange={(m) => setMetoda(m as TargetareMetodaSursa)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GET">Descărcare directă (GET)</SelectItem>
                <SelectItem value="POST">
                  Formular de export (POST, cu token din pagină)
                </SelectItem>
                <SelectItem value="GET_PAGINAT">
                  Pagini HTML numerotate (adresa conține {"{pagina}"})
                </SelectItem>
                <SelectItem value="BNR">
                  Registru BNR (pagina își încarcă blocurile prin POST)
                </SelectItem>
                <SelectItem value="ANCOM">
                  Listă ANCOM (formular paginat + pagini de detaliu cu CUI)
                </SelectItem>
              </SelectContent>
            </Select>
          </label>
          {metoda === "POST" && (
            <>
              <label className="space-y-1 text-xs">
                <span className="text-muted-foreground">
                  Pagina din care se ia token-ul și sesiunea
                </span>
                <Input
                  value={pagina}
                  onChange={(e) => setPagina(e.target.value)}
                  placeholder="https://portal.anre.ro/PublicLists/LicenteAutorizatii"
                  className="h-8 font-mono text-xs"
                />
              </label>
              <label className="space-y-1 text-xs md:col-span-2">
                <span className="text-muted-foreground">
                  Câmpurile formularului (câte unul pe linie, nume=valoare;
                  token-ul se adaugă automat)
                </span>
                <Textarea
                  value={campuri}
                  onChange={(e) => setCampuri(e.target.value)}
                  rows={3}
                  className="font-mono text-xs"
                />
              </label>
            </>
          )}
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Numele sursei</span>
            <Input
              value={sursaNume}
              onChange={(e) => setSursaNume(e.target.value)}
              placeholder="ex. ANRE – Licențe și autorizații"
              className="h-8 text-xs"
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Autoritatea</span>
            <Input
              value={autoritate}
              onChange={(e) => setAutoritate(e.target.value)}
              placeholder="ex. ANRE, ANCOM, BNR"
              className="h-8 text-xs"
            />
          </label>
          <label className="space-y-1 text-xs md:col-span-2">
            <span className="text-muted-foreground">
              Coloana din fișier care determină serviciul (opțional; maparea
              valorilor se face la import)
              {lista?.mapare_serviciu &&
                ` · mapare salvată: ${Object.keys(lista.mapare_serviciu).length} valori`}
            </span>
            <Input
              value={coloanaServiciu}
              onChange={(e) => setColoanaServiciu(e.target.value)}
              placeholder="ex. TipActivitate"
              className="h-8 text-xs"
            />
          </label>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <Switch
              checked={automata}
              onCheckedChange={setAutomata}
              disabled={!sursaUrl.trim()}
            />
            Actualizare automată lunară de la sursă (pe server)
            {lista?.actualizare_la && (
              <span className="text-xs text-muted-foreground">
                · ultima rulare {formatDataOra(lista.actualizare_la)}:{" "}
                {lista.actualizare_stare}
                {lista.actualizare_mesaj && ` – ${lista.actualizare_mesaj}`}
              </span>
            )}
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">
              Foaia din fișierul Excel al sursei (dacă are mai multe)
            </span>
            <Input
              value={foaie}
              onChange={(e) => setFoaie(e.target.value)}
              placeholder="ex. Sheet1"
              className="h-8 text-xs"
            />
          </label>
          <p className="self-end pb-1 text-xs text-muted-foreground">
            Coloanele folosite la ultimul import:{" "}
            {lista?.mapare_coloane
              ? Object.entries(lista.mapare_coloane)
                  .filter(([, v]) => v)
                  .map(([k, v]) => `${k} = ${v}`)
                  .join(", ") || "detectate automat"
              : "detectate automat"}
          </p>
          <label className="space-y-1 text-xs md:col-span-2">
            <span className="text-muted-foreground">
              Observații (cum se preia lista, ce coloane are, ce trebuie
              curățat)
            </span>
            <Textarea
              value={descriere}
              onChange={(e) => setDescriere(e.target.value)}
              rows={4}
            />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Renunță
          </Button>
          <Button onClick={confirma} disabled={salveaza.isPending || !lista}>
            {salveaza.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Salvează
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
